import { EXERCISE_TYPE } from '../constants';

/**
 * Determines which per-assignment text fields an item supports.
 *
 * Calendar items carry their type in a few shapes: a short letter code
 * (EXERCISE_TYPE, e.g. 'L', 'CD'), a verbose `typeDescription`/`type` string
 * (e.g. 'lift', 'cooldown'), and — for saved items — the type lives on
 * `item.exercise.type`. We test all of those against exact-string sets so that
 * 'C' (Circuit) is never confused with 'CD' (Cooldown).
 *
 * @param {object} item - a calendar content / modalData object
 * @returns {'additionalInfo' | 'description' | 'both' | 'none'}
 */

// Sets are exact-match only — never prefix-match (C vs CD).
const ADDITIONAL_INFO_ONLY = new Set([
  EXERCISE_TYPE.Lift, // 'L'
  EXERCISE_TYPE.Tag, // 'T'
  EXERCISE_TYPE.Questionnaire, // 'S'
  'lift', 'tag', 'questionnaire',
]);

const DESCRIPTION_ONLY = new Set([
  EXERCISE_TYPE.WarmUp, // 'W'
  EXERCISE_TYPE.Note, // 'N'
  EXERCISE_TYPE.Cooldown, // 'CD'
  'warm_up', 'note', 'cooldown',
]);

const BOTH = new Set([
  EXERCISE_TYPE.RunCardio, // 'R' (SAQ+C / cardio)
  EXERCISE_TYPE.Circuit, // 'C'
  'cardio', 'circuit',
]);

const NONE = new Set([
  EXERCISE_TYPE.Health, // 'H'
  EXERCISE_TYPE.Video, // 'V'
  EXERCISE_TYPE.Rest, // 'RT'
  EXERCISE_TYPE.SessionBreak, // 'BR'
  EXERCISE_TYPE.Progression, // 'P'
  'health', 'video', 'rest', 'session_break', 'progression',
]);

const resolveType = (item) => {
  if (!item) {
    return undefined;
  }
  if (item.type === 'session_break') {
    return 'session_break';
  }
  if (item.type === 'rest') {
    return 'rest';
  }
  if (item.preSave) {
    return item.type;
  }
  return item.exercise?.type ?? item.type;
};

const getExerciseInfoCategory = (item) => {
  if (!item) {
    return 'none';
  }
  const candidates = [
    item.type,
    item.typeDescription,
    resolveType(item),
    item.exercise?.type,
    item.exercise?.typeDescription,
  ].filter(Boolean);

  // Order matters: BOTH and NONE win over the single-field sets.
  if (candidates.some((c) => BOTH.has(c))) {
    return 'both';
  }
  if (candidates.some((c) => NONE.has(c))) {
    return 'none';
  }
  if (candidates.some((c) => ADDITIONAL_INFO_ONLY.has(c))) {
    return 'additionalInfo';
  }
  if (candidates.some((c) => DESCRIPTION_ONLY.has(c))) {
    return 'description';
  }
  return 'none';
};

export default getExerciseInfoCategory;
